import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plus, Pencil, Trash2, Building } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

export default function OwnersPage() {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", nif: "", address: "", floor_door: "", phone: "", email: "", monthly_fee: "", is_commercial: false, active: true, notes: "" });

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/community/owners`); setOwners(data); }
    catch (e) { if (e.response?.status !== 401) toast.error("Error cargando"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", nif: "", address: "", floor_door: "", phone: "", email: "", monthly_fee: "", is_commercial: false, active: true, notes: "" }); setDialogOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ name: o.name, nif: o.nif, address: o.address, floor_door: o.floor_door, phone: o.phone || "", email: o.email || "", monthly_fee: String(o.monthly_fee), is_commercial: o.is_commercial || false, active: o.active, notes: o.notes || "" }); setDialogOpen(true); };

  const handleSave = async () => {
    const payload = { ...form, monthly_fee: parseFloat(form.monthly_fee) || 0 };
    try {
      if (editing) { await axios.put(`${API}/community/owners/${editing.id}`, payload); toast.success("Propietario actualizado"); }
      else { await axios.post(`${API}/community/owners`, payload); toast.success("Propietario creado"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error guardando"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este propietario?")) return;
    try { await axios.delete(`${API}/community/owners/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="owners-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Propietarios</h2>
          <p className="text-sm text-gray-500 mt-1">{owners.length} propietarios</p>
        </div>
        <button onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2" data-testid="add-owner-button">
          <Plus className="w-4 h-4" /> Nuevo propietario
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-sm" data-testid="owners-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Piso/Puerta</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Nombre</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">NIF</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Telefono</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Cuota/mes</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Tipo</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {owners.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No hay propietarios</TableCell></TableRow>
            ) : owners.map(o => (
              <TableRow key={o.id} className="hover:bg-gray-50">
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4">{o.floor_door}</TableCell>
                <TableCell className="text-sm text-gray-900 py-3 px-4">{o.name}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{o.nif}</TableCell>
                <TableCell className="text-sm text-gray-600 py-3 px-4">{o.phone || "—"}</TableCell>
                <TableCell className="text-sm font-medium text-gray-900 py-3 px-4 text-right">{formatCurrency(o.monthly_fee)}</TableCell>
                <TableCell className="py-3 px-4">
                  {o.is_commercial ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold bg-violet-50 text-violet-700 border border-violet-200"><Building className="w-3 h-3 mr-1" />Comercial</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">Vivienda</span>
                  )}
                </TableCell>
                <TableCell className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(o)} className="p-1.5 text-gray-400 hover:text-[#002FA7] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(o.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="owner-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar propietario" : "Nuevo propietario"}</DialogTitle>
            <DialogDescription>Datos del propietario de la comunidad</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Nombre</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-sm" data-testid="owner-name-input" /></div>
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">NIF</Label><Input value={form.nif} onChange={e => setForm({...form, nif: e.target.value})} className="rounded-sm" data-testid="owner-nif-input" /></div>
            </div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Direccion</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="rounded-sm" data-testid="owner-address-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Piso / Puerta</Label><Input value={form.floor_door} onChange={e => setForm({...form, floor_door: e.target.value})} placeholder="Ej: 1A, Bajo" className="rounded-sm" data-testid="owner-floor-input" /></div>
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Cuota mensual</Label><Input type="number" step="0.01" value={form.monthly_fee} onChange={e => setForm({...form, monthly_fee: e.target.value})} className="rounded-sm" data-testid="owner-fee-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Telefono</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="rounded-sm" data-testid="owner-phone-input" /></div>
              <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-sm" data-testid="owner-email-input" /></div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.is_commercial} onChange={e => setForm({...form, is_commercial: e.target.checked})} className="rounded" /><span>Local comercial (bajo)</span></label>
            </div>
            <div><Label className="text-sm font-semibold text-gray-900 mb-1 block">Notas</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notas adicionales..." className="rounded-sm" data-testid="owner-notes-input" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all">Cancelar</button>
            <button onClick={handleSave} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="owner-save-button">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
